import { eulerConfigs, eulerVaults } from '@1delta/data-sdk'
import type { Address } from 'viem'
import type { AdditionalYields } from '../../../types'
import type { GenericCurrency, GenericTokenList } from '../../../types/general'
import { toOracleKey, toGenericPriceKey } from '../../../assets'
import { createMarketUid } from '../../../utils'
import { apyToApr } from '../../../utils/parsing'
import { deriveClusters } from './fetcher/cluster'
import { normalizeCluster } from './fetcher/normalize'
import type { VaultInfoFull } from './fetcher/types'

export const getEulerV2ReservesDataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => { data: any; chainId: string } | undefined, number] => {
  const config = eulerConfigs()?.[lender]?.[chainId]
  const vaultAddresses = eulerVaults()?.[lender]?.[chainId] ?? []
  if (!config || vaultAddresses.length === 0) return [() => undefined, 0]
  const expectedNumberOfCalls = vaultAddresses.length

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }

      // Reconstruct Map<Address, VaultInfoFull> from raw multicall results
      const vaultDataMap = new Map<Address, VaultInfoFull>()
      for (let i = 0; i < vaultAddresses.length; i++) {
        const result = data[i]
        if (result) {
          vaultDataMap.set(
            vaultAddresses[i].vault.toLowerCase() as Address,
            result as VaultInfoFull,
          )
        }
      }

      if (vaultDataMap.size === 0) return undefined

      // Cluster and normalize
      const clusters = deriveClusters(vaultDataMap, true)
      const resultReserves: any = {}

      for (const cluster of clusters) {
        const normalized = normalizeCluster(cluster, {
          chainId,
          tokenList: tokenList as any,
        })

        for (const [vaultAddr, entry] of Object.entries(normalized.data)) {
          const marketUid = createMarketUid(chainId, lender, vaultAddr)

          // Resolve price from prices map
          const assetMeta: GenericCurrency | undefined =
            tokenList[entry.asset.address]
          const oracleKey =
            toOracleKey(assetMeta?.assetGroup ?? null) ||
            toGenericPriceKey(entry.asset.address, chainId)
          let price = prices[oracleKey] ?? 0

          // If no external price, derive from Euler's internal oracle
          // and populate the prices map for downstream consumers (leverage pairs)
          if (
            price === 0 &&
            entry.totalDeposits > 0 &&
            entry.totalDepositsUSD > 0
          ) {
            const derivedPrice =
              entry.totalDepositsUSD / entry.totalDeposits
            if (derivedPrice > 0 && isFinite(derivedPrice)) {
              price = derivedPrice
              prices[oracleKey] = derivedPrice
            }
          }

          const totalDeposits = entry.totalDeposits
          const totalDebt = entry.totalDebt
          const totalLiquidity = totalDeposits - totalDebt

          // Override USD values with external prices if available
          const totalDepositsUSD =
            price > 0 ? totalDeposits * price : entry.totalDepositsUSD
          const totalDebtUSD =
            price > 0 ? totalDebt * price : entry.totalDebtUSD
          const totalLiquidityUSD =
            price > 0 ? totalLiquidity * price : totalDepositsUSD - totalDebtUSD

          resultReserves[marketUid] = {
            marketUid,
            name: entry.name,
            poolId: vaultAddr,
            underlying: entry.asset.address,
            asset: {
              ...{
                chainId,
                decimals: entry.asset.decimals,
                name: entry.asset.name,
                address: entry.asset.address,
                symbol: entry.asset.symbol,
              },
              ...(assetMeta ?? {}),
            },
            // token amounts
            totalDeposits: String(totalDeposits),
            totalDebtStable: String(entry.totalDebtStable),
            totalDebt: String(totalDebt),
            totalLiquidity: String(totalLiquidity),
            ...(() => {
              const bc = entry.borrowCap
              const bl = bc > 0
                ? Math.min(totalLiquidity, Math.max(0, bc - totalDebt))
                : totalLiquidity
              return {
                borrowLiquidity: bl,
                borrowLiquidityUSD: bl * price,
              }
            })(),
            // USD amounts
            totalDepositsUSD,
            totalDebtStableUSD: entry.totalDebtStableUSD,
            totalDebtUSD,
            totalLiquidityUSD,
            accumulator: entry.accumulator,
            // rates (normalizer yields APY as decimal, convert to APR percentage)
            depositRate: apyToApr(entry.depositRate) * 100,
            variableBorrowRate: apyToApr(entry.variableBorrowRate) * 100,
            stableBorrowRate: entry.stableBorrowRate,
            intrinsicYield: additionalYields?.intrinsicYields?.[oracleKey] ?? 0,
            // rewards
            rewards: [],
            decimals: entry.decimals,
            // config (keyed by counterparty vault address)
            config: entry.config,
            // Euler liquidation is bounded by a discount curve rather than a
            // fixed close factor — a position can be fully liquidated.
            closeFactor: 1,
            // flags
            collateralActive: entry.collateralActive,
            borrowingEnabled: entry.borrowingEnabled,
            depositsEnabled: entry.depositsEnabled,
            hasStable: entry.hasStable,
            isActive: entry.isActive,
            isFrozen: entry.isFrozen,
            // caps
            borrowCap: entry.borrowCap,
            supplyCap: entry.supplyCap,
            debtCeiling: entry.debtCeiling,
            // metadata
            params: {
              metadata: entry.params.metadata,
            },
          }
        }
      }

      return {
        data: resultReserves,
        chainId,
      }
    },
    expectedNumberOfCalls,
  ]
}
